import { RaidPlanAssignmentEntity } from 'app/frontend/types/custom/RaidPlan'
import { VisualShape } from 'app/frontend/types/custom/VisualPlan'
import {
  createResolvedEntityBadge,
  fillEmptyVisualPlanSlots,
  ResolvedEntityBadge,
  VisualEntitySlot,
} from '../../TeamPlans/components/visual_plans/shapes/entityAssignmentMatching'
import {
  EventPlanVisualEntityOverrides,
  visualEntitySlotKey,
} from '../components/eventActivityPlanOverrides'

type SpecializationInfo = {
  name: string
  icon: string
  classId: number
  className: string
}

type ApplyVisualPlanEntityOverridesInput = {
  assignments: Map<number, ResolvedEntityBadge>
  slots: VisualEntitySlot[]
  shapes: VisualShape[]
  screenId: string
  overrides?: EventPlanVisualEntityOverrides
  entities: RaidPlanAssignmentEntity[]
  specializationById: Record<number, SpecializationInfo>
  memberInfoByCharacterId: Record<number, { name: string; className: string }>
}

export const applyVisualPlanEntityOverrides = ({
  assignments,
  slots,
  shapes,
  screenId,
  overrides,
  entities,
  specializationById,
  memberInfoByCharacterId,
}: ApplyVisualPlanEntityOverridesInput): Map<number, ResolvedEntityBadge> => {
  if (!overrides || Object.keys(overrides).length === 0) return assignments

  const nextAssignments = new Map(assignments)
  const pinnedEmpty = new Set<number>()
  const pinnedCharacterIds = new Set<number>()

  slots.forEach((slot) => {
    const override =
      overrides[visualEntitySlotKey(screenId, slot.shapeId, slot.localIndex)]
    if (!override) return

    if (override.kind === 'empty') {
      nextAssignments.delete(slot.number)
      pinnedEmpty.add(slot.number)
      return
    }

    if (override.kind === 'participant') {
      const entityIndex = entities.findIndex(
        (entity) =>
          entity.kind === 'member' &&
          entity.character_id === override.characterId,
      )
      const entity = entityIndex >= 0 ? entities[entityIndex] : undefined
      if (!entity) return
      nextAssignments.set(
        slot.number,
        createResolvedEntityBadge(
          entity,
          slot.number - 1,
          specializationById,
          memberInfoByCharacterId,
          { isOverridden: true },
        ),
      )
      pinnedCharacterIds.add(override.characterId)
      return
    }

    nextAssignments.set(
      slot.number,
      createResolvedEntityBadge(
        override.entity,
        slot.number - 1,
        specializationById,
        memberInfoByCharacterId,
        { isOverridden: true },
      ),
    )
  })

  nextAssignments.forEach((badge, number) => {
    if (badge.isOverridden) return
    if (badge.characterId == null) return
    if (!pinnedCharacterIds.has(badge.characterId)) return
    nextAssignments.delete(number)
  })

  return fillEmptyVisualPlanSlots({
    shapes,
    entities,
    specializationById,
    memberInfoByCharacterId,
    assignments: nextAssignments,
    skipSlotNumbers: pinnedEmpty,
  })
}
